import React from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { GRN_MODES, GRN_RECEIPT_MODE_OPTIONS } from '../../constants/grnManagement';
import { IPD_COLORS, cardSx } from '../../theme';

const MODE_ICONS = {
  [GRN_MODES.PO]: AssignmentIcon,
  [GRN_MODES.DIRECT]: LocalShippingIcon,
};

/**
 * Receipt-type fork for GRN wizard step 0.
 * Card layout on desktop; stacked full-width on mobile.
 */
export default function GrnReceiptModeSelector({ value, onChange, disabledModes = [] }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      role="radiogroup"
      aria-label="Receipt type"
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: 2,
        mb: 3,
      }}
    >
      {GRN_RECEIPT_MODE_OPTIONS.map((option) => {
        const selected = value === option.value;
        const disabled = disabledModes.includes(option.value);
        const Icon = MODE_ICONS[option.value];

        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => !disabled && onChange(option.value)}
            sx={{
              ...cardSx,
              p: 2.5,
              textAlign: 'left',
              cursor: disabled ? 'not-allowed' : 'pointer',
              border: `2px solid ${selected ? IPD_COLORS.primary : IPD_COLORS.border}`,
              backgroundColor: selected ? `${IPD_COLORS.primary}10` : IPD_COLORS.surface,
              opacity: disabled ? 0.55 : 1,
              transition: 'border-color 0.2s, background-color 0.2s, box-shadow 0.2s',
              '&:hover': disabled ? undefined : {
                borderColor: IPD_COLORS.primary,
                boxShadow: `0 4px 14px ${IPD_COLORS.primary}18`,
              },
              '&:focus-visible': {
                outline: `2px solid ${IPD_COLORS.primary}`,
                outlineOffset: 2,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                backgroundColor: selected ? `${IPD_COLORS.primary}20` : IPD_COLORS.selectedBg,
                color: selected ? IPD_COLORS.primary : IPD_COLORS.textSecondary,
              }}>
                <Icon fontSize="small" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                  <Typography sx={{
                    color: IPD_COLORS.textPrimary,
                    fontWeight: 700,
                    fontSize: 14,
                  }}>
                    {option.title}
                  </Typography>
                  {selected && (
                    <CheckCircleIcon sx={{ color: IPD_COLORS.primary, fontSize: 18 }} />
                  )}
                </Box>
                <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 12, lineHeight: 1.5 }}>
                  {option.description}
                </Typography>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
